import { z } from 'zod';
import type { TracePreviewDto } from '@terra/shared-contracts-trace';
import { agentExecutionClient } from '@terra/shared-rest-clients-agent-execution';
import { isDeepActiveProbeTrace } from '@terra/ui-design-system';
import { terraProxyApiClient } from '../clients/testerClient';
import { resumeNeoAttack, runNeoAttack } from './trace-action-helpers';

export type TraceActionTarget = Pick<
  TracePreviewDto,
  'name' | 'threadId' | 'signalId' | 'executionId'
>;

type TraceActionInput = {
  trace: TraceActionTarget;
  applicationId: string;
};

export type TraceRerunResult = { started: boolean; error?: string };

const ErrorResponseBodySchema = z.object({ message: z.string().min(1) });

const assertSucceeded = ({
  response,
  fallbackMessage,
}: {
  response: { status: number; body: unknown };
  fallbackMessage: string;
}) => {
  if (response.status === 200) {
    return;
  }

  const parsed = ErrorResponseBodySchema.safeParse(response.body);

  throw new Error(parsed.success ? parsed.data.message : fallbackMessage);
};

const requireExecutionId = (trace: TraceActionTarget): string => {
  if (!trace.executionId) {
    throw new Error('This run is not linked to an execution');
  }

  return trace.executionId;
};

export async function resumeTraceExecution({
  trace,
  applicationId,
}: TraceActionInput): Promise<void> {
  if (isDeepActiveProbeTrace(trace)) {
    assertSucceeded({
      response: await agentExecutionClient.resumeDeepActiveProbe({
        params: { executionId: requireExecutionId(trace) },
        body: {},
      }),
      fallbackMessage: 'Failed to resume the execution',
    });

    return;
  }

  await resumeNeoAttack({ threadId: trace.threadId, applicationId });
}

export async function rerunTraceExecution({
  trace,
  applicationId,
}: TraceActionInput): Promise<TraceRerunResult> {
  if (isDeepActiveProbeTrace(trace)) {
    assertSucceeded({
      response: await agentExecutionClient.rerunDeepActiveProbe({
        params: { executionId: requireExecutionId(trace) },
        body: {},
      }),
      fallbackMessage: 'Failed to start the execution',
    });

    return { started: true };
  }

  if (!trace.signalId) {
    return { started: false };
  }

  const data = await runNeoAttack({ signalId: trace.signalId, applicationId });

  return data.threadId
    ? { started: true }
    : { started: false, ...(data.error && { error: data.error }) };
}

export async function abortTraceExecution({
  trace,
  applicationId,
}: TraceActionInput): Promise<void> {
  if (isDeepActiveProbeTrace(trace)) {
    assertSucceeded({
      response: await agentExecutionClient.abort({
        params: { executionId: requireExecutionId(trace) },
        body: {},
      }),
      fallbackMessage: 'Failed to abort the execution',
    });

    return;
  }

  await terraProxyApiClient.abortNeoExecution({
    threadId: trace.threadId,
    applicationId,
  });
}
